import React, { useState, useEffect } from 'react';
import { API_BASE } from '../../config';

/**
 * Login History Card — shows recent login events with location data.
 * Part of the Security Activity Page (#66).
 * Reads from GET /api/auth/login-history (geo-IP enriched).
 */
function LoginHistoryCard({ fetchWithAuth }) {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dateRange, setDateRange] = useState('7d');

  useEffect(() => {
    loadHistory();
  }, [dateRange]);

  const loadHistory = async () => {
    setLoading(true);
    try {
      const response = await fetchWithAuth(`${API_BASE}/api/auth/login-history?limit=100&date_range=${dateRange}`);
      if (response.ok) {
        const data = await response.json();
        setEvents(data.events || []);
      }
    } catch (err) {
      // Silently fail
    } finally {
      setLoading(false);
    }
  };

  const formatTime = (timestamp) => {
    if (!timestamp) return '—';
    const d = new Date(timestamp);
    return d.toLocaleString();
  };

  const formatLocation = (event) => {
    const parts = [];
    if (event.city) parts.push(event.city);
    if (event.region) parts.push(event.region);
    if (event.country_code) parts.push(event.country_code);
    return parts.length > 0 ? parts.join(', ') : '—';
  };

  const getEventLabel = (eventType) => {
    switch (eventType) {
      case 'AUTH_LOGIN': return 'Sign in';
      case 'AUTH_FAILED': return 'Failed login';
      case 'AUTH_NEW_DEVICE': return 'New device';
      case 'AUTH_LOGOUT': return 'Sign out';
      default: return eventType;
    }
  };

  return (
    <div className="card mb-4">
      <div className="card-header" style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center'}}>
        <h3 className="card-title">Login History</h3>
        <select
          className="form-select lhc-dateRange"
          value={dateRange}
          onChange={(e) => setDateRange(e.target.value)}
        >
          <option value="7d">Last 7 Days</option>
          <option value="30d">Last 30 Days</option>
          <option value="90d">Last 90 Days</option>
          <option value="1y">Last 1 Year</option>
        </select>
      </div>
      <div className="p-4">
        {loading ? (
          <p className="text-muted">Loading login history...</p>
        ) : events.length === 0 ? (
          <p className="text-muted">No login events recorded yet.</p>
        ) : (
          <div className="lhc-table-wrap">
            <table className="lhc-table">
              <thead>
                <tr>
                  <th>Date / Time</th>
                  <th>Event</th>
                  <th>Location</th>
                  <th>Device</th>
                  <th>IP</th>
                </tr>
              </thead>
              <tbody>
                {events.map((event, i) => (
                  <tr key={i} className={event.result === 'FAILURE' ? 'lhc-row-failed' : ''}>
                    <td>{formatTime(event.timestamp)}</td>
                    <td>
                      <span className={`lhc-badge ${event.result === 'FAILURE' ? 'lhc-badge-fail' : 'lhc-badge-ok'}`}>
                        {getEventLabel(event.event_type)}
                      </span>
                    </td>
                    <td>{formatLocation(event)}</td>
                    <td>{event.device || '—'}</td>
                    <td className="lhc-ip">{event.ip_masked || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

export default LoginHistoryCard;
